import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2 } from "lucide-react";

/** Records mic audio and returns a Whisper transcription via onText(text). */
export function VoiceDictate({ onText, size = "sm", testid = "voice-dictate-btn" }) {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) { setState("idle"); return; }
        setState("processing");
        try {
          const ext = type.includes("mp4") ? "mp4" : "webm";
          const fd = new FormData();
          fd.append("file", blob, `dictation.${ext}`);
          const { data } = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) { onText(data.text); toast.success("Added your dictation"); }
          else toast.error("No speech detected — try again");
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        } finally { setState("idle"); }
      };
      recorderRef.current = mr;
      mr.start();
      setState("recording");
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => {
    if (recorderRef.current && state === "recording") recorderRef.current.stop();
  };

  if (state === "processing") {
    return (
      <Button type="button" size={size} variant="outline" disabled data-testid={testid} className="gap-1.5">
        <Loader2 className="h-4 w-4 animate-spin" /> Transcribing…
      </Button>
    );
  }
  if (state === "recording") {
    return (
      <Button type="button" size={size} variant="outline" onClick={stop} data-testid={`${testid}-stop`}
        className="gap-1.5 border-rose-500/40 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20">
        <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" /></span>
        <Square className="h-3.5 w-3.5" /> Stop & insert
      </Button>
    );
  }
  return (
    <Button type="button" size={size} variant="outline" onClick={start} data-testid={testid} className="gap-1.5">
      <Mic className="h-4 w-4" /> Dictate
    </Button>
  );
}
